import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	EventEmitter,
	Input,
	OnChanges,
	OnInit,
	Output,
	SimpleChanges
} from '@angular/core';
import { CommonModule, NgIf } from '@angular/common';
import { FormBuilder, FormControl, ReactiveFormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { filter, takeUntil } from 'rxjs/operators';

import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { SelectComponent } from '@shared/components/select/select.component';
import { IsDisabledDirective } from '@shared/directives/is-disabled/is-disabled.directive';
import { DropDownItem } from '@shared/models/drop-down-item';
import { IdName } from '@shared/models/id-name';
import { MaterialOption } from '@shared/models/material-option';
import { BaseDestroyableComponent } from '@shared/base-classes/base-destroyable';
import { RxMaterialRulesService } from '@shared/services/rx-rules-helper/rx-material-rules.service';
import { RxShadeSystemRulesService } from '@shared/services/rx-rules-helper/rx-shade-system-rules.service';
import { getMeaningfulValue } from '@shared/utils/type-util';
import { Maybe } from '@/types/globals';
import { AdditionalInfoIds, AdditionalInfoFieldKey } from '@modules/rx-intuitive-crown-additional-info/models/additional-info';

export const mapPreparationDesignOptionsFromPrepValues = (prepDesignValues: string[] | undefined, preparationDesigns: IdName[]): IdName[] =>
	prepDesignValues?.map(prepDesignId => ({
		Id: prepDesignId,
		Name: preparationDesigns.find(o => o.Id === prepDesignId)?.Name ?? ''
	})) ?? [];

interface AdditionalFieldsFormValue {
	preparationDesignBuccal: DropDownItem<string> | null;
	preparationDesignLingual: DropDownItem<string> | null;
	marginDesignBuccal: DropDownItem<string> | null;
	marginDesignLingual: DropDownItem<string> | null;
	incisal: DropDownItem<string> | string | null;
	gingival: DropDownItem<string> | string | null;
	stumpShade: DropDownItem<string> | null;
}

@Component({
	selector: 'rx-intuitive-crown-additional-fields',
	templateUrl: './rx-intuitive-crown-additional-fields.component.html',
	styleUrls: ['./rx-intuitive-crown-additional-fields.component.scss'],
	standalone: true,
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [
		CommonModule,
		NgIf,
		ReactiveFormsModule,
		TranslateModule,
		SelectComponent,
		MatFormFieldModule,
		MatInputModule,
		IsDisabledDirective
	]
})
export class RxIntuitiveCrownAdditionalFieldsComponent extends BaseDestroyableComponent implements OnInit, OnChanges {
	readonly maxInputLengthForOtherShadeSystem = 10;

	@Input() materialOption: MaterialOption | undefined;
	@Input() materialIdForShadeOptions: Maybe<number | string>;
	@Input() shadeSystemId: Maybe<number>;
	@Input() isOtherShadeSystemSelected = false;
	@Input() initialValues: AdditionalInfoIds | null = null;
	@Input() isReadOnly = false;
	@Input() shouldValidateForSend = false;

	/** Prefix for control `id` attributes so multiple instances stay unique in the DOM. */
	@Input() controlIdPrefix = 'intuitive-additional';

	@Output() readonly additionalInfoChange = new EventEmitter<AdditionalInfoIds>();
	@Output() readonly clearedFieldsChange = new EventEmitter<AdditionalInfoFieldKey[]>();

	readonly form = this.formBuilder.group<AdditionalFieldsFormValue>({
		preparationDesignBuccal: null,
		preparationDesignLingual: null,
		marginDesignBuccal: null,
		marginDesignLingual: null,
		incisal: null,
		gingival: null,
		stumpShade: null
	});

	preparationDesignOptions: IdName[] = [];
	marginDesignOptions: IdName[] = [];
	incisalBodyGingivalOptions: IdName<string>[] = [];
	stumpShadeOptions: IdName[] = [];

	private readonly clearedFields = new Set<AdditionalInfoFieldKey>();

	constructor(
		private readonly formBuilder: FormBuilder,
		private readonly rxMaterialRulesService: RxMaterialRulesService,
		private readonly rxShadeSystemRulesService: RxShadeSystemRulesService,
		private readonly cdr: ChangeDetectorRef
	) {
		super();
	}

	ngOnInit(): void {
		this.syncFormFromInputs();

		this.form.valueChanges
			.pipe(
				filter(() => !this.isReadOnly),
				takeUntil(this.componentAlive$)
			)
			.subscribe(() => {
				this.emitAdditionalInfo();
			});
	}

	ngOnChanges(changes: SimpleChanges): void {
		const relevantKeys = [
			'materialOption',
			'materialIdForShadeOptions',
			'shadeSystemId',
			'isOtherShadeSystemSelected',
			'initialValues',
			'isReadOnly'
		];

		if (!Object.keys(changes).some(k => relevantKeys.includes(k))) {
			return;
		}

		const relevantChanges = Object.entries(changes).filter(([k]) => relevantKeys.includes(k));

		if (relevantChanges.length > 0 && relevantChanges.every(([, c]) => c.firstChange)) {
			return;
		}

		this.syncFormFromInputs();
	}

	get preparationDesignBuccalControl() {
		return this.form.controls.preparationDesignBuccal;
	}

	get preparationDesignLingualControl() {
		return this.form.controls.preparationDesignLingual;
	}

	get marginDesignBuccalControl() {
		return this.form.controls.marginDesignBuccal;
	}

	get marginDesignLingualControl() {
		return this.form.controls.marginDesignLingual;
	}

	get incisalControl() {
		return this.form.controls.incisal;
	}

	get gingivalControl() {
		return this.form.controls.gingival;
	}

	get stumpShadeControl() {
		return this.form.controls.stumpShade;
	}

	get incisalControlAsIdName(): FormControl<DropDownItem<string>> {
		return this.incisalControl as FormControl<DropDownItem<string>>;
	}

	get gingivalControlAsIdName(): FormControl<DropDownItem<string>> {
		return this.gingivalControl as FormControl<DropDownItem<string>>;
	}

	get isShadeDisabled(): boolean {
		return !this.materialOption?.ShadeVisible;
	}

	/** Incisal / gingival: disabled when shade column is off or form is read-only. */
	get shadeIncisalGingivalDisabled(): boolean {
		return this.isShadeDisabled || this.isReadOnly;
	}

	get preparationDesignSelectRequired(): boolean {
		return this.shouldValidateForSend && !!this.materialOption?.PrepDesignMandatory;
	}

	get preparationDesignSelectCancellable(): boolean {
		return !this.isReadOnly && (this.shouldValidateForSend === false || !this.materialOption?.PrepDesignMandatory);
	}

	get preparationDesignSelectDisabled(): boolean {
		return !this.materialOption?.PrepDesignVisible || this.isReadOnly;
	}

	get marginDesignSelectRequired(): boolean {
		return this.shouldValidateForSend && !!this.materialOption?.MarginDesignMandatory;
	}

	get marginDesignSelectCancellable(): boolean {
		return !this.isReadOnly && (this.shouldValidateForSend === false || !this.materialOption?.MarginDesignMandatory);
	}

	get marginDesignSelectDisabled(): boolean {
		return !this.materialOption?.MarginDesignVisible || this.isReadOnly;
	}

	get stumpShadeSelectRequired(): boolean {
		return this.shouldValidateForSend && !!this.materialOption?.StumpShadeMandatory;
	}

	get stumpShadeSelectCancellable(): boolean {
		return !this.isReadOnly && (this.shouldValidateForSend === false || !this.materialOption?.StumpShadeMandatory);
	}

	get stumpShadeSelectDisabled(): boolean {
		return !this.materialOption?.StumpShadeVisible || this.isReadOnly;
	}

	onFieldCleared(field: AdditionalInfoFieldKey): void {
		if (this.isReadOnly) {
			return;
		}

		this.clearedFields.add(field);
		this.clearedFieldsChange.emit([...this.clearedFields]);
	}

	private syncFormFromInputs(): void {
		this.rebuildOptionLists();

		if (this.initialValues) {
			this.patchFormFromInitialValues(this.initialValues);
		} else {
			this.form.reset(undefined, { emitEvent: false });
		}

		this.emitAdditionalInfo();
		this.cdr.markForCheck();
	}

	private rebuildOptionLists(): void {
		this.preparationDesignOptions = mapPreparationDesignOptionsFromPrepValues(
			this.materialOption?.PrepDesignValues,
			this.rxMaterialRulesService.getPreparationDesignOptionsMapping()
		);
		this.marginDesignOptions = this.rxMaterialRulesService.getMarginDesignOptions();
		this.stumpShadeOptions = this.rxShadeSystemRulesService.getStumpShadeOptions() ?? [];

		const shadeSystemId = this.shadeSystemId;

		if (shadeSystemId == null) {
			this.incisalBodyGingivalOptions = [];
		} else {
			const parsedMaterial = this.materialIdForShadeOptions != null ? parseInt(String(this.materialIdForShadeOptions), 10) : -1;

			this.incisalBodyGingivalOptions =
				this.rxShadeSystemRulesService.getIncisalBodyGingivalOptions({ materialId: parsedMaterial, shadeSystemId }) ?? [];
		}
	}

	private patchFormFromInitialValues(ids: AdditionalInfoIds): void {
		const prepBuccal = this.preparationDesignOptions.find(o => o.Id === ids.PreparationDesignBuccalId) ?? null;
		const prepLingual = this.preparationDesignOptions.find(o => o.Id === ids.PreparationDesignLingualId) ?? null;
		const marginBuccal = this.marginDesignOptions.find(o => o.Id === ids.MarginDesignBuccalId) ?? null;
		const marginLingual = this.marginDesignOptions.find(o => o.Id === ids.MarginDesignLingualId) ?? null;

		let incisalVal: IdName | string | null = null;
		let gingivalVal: IdName | string | null = null;

		if (this.isOtherShadeSystemSelected) {
			incisalVal = ids.ShadeIncisal ?? null;
			gingivalVal = ids.ShadeGingival ?? null;
		} else {
			incisalVal = this.incisalBodyGingivalOptions.find(o => o.Id === ids.ShadeIncisal) ?? null;
			gingivalVal = this.incisalBodyGingivalOptions.find(o => o.Id === ids.ShadeGingival) ?? null;
		}

		const stump = this.stumpShadeOptions.find(s => s.Id === ids.StumpfShade) ?? null;

		this.form.patchValue(
			{
				preparationDesignBuccal: prepBuccal,
				preparationDesignLingual: prepLingual,
				marginDesignBuccal: marginBuccal,
				marginDesignLingual: marginLingual,
				incisal: incisalVal,
				gingival: gingivalVal,
				stumpShade: stump
			},
			{ emitEvent: false }
		);
	}

	private emitAdditionalInfo(): void {
		const raw = this.form.getRawValue();

		this.additionalInfoChange.emit({
			PreparationDesignBuccalId: raw.preparationDesignBuccal?.Id ?? null,
			PreparationDesignBuccal: raw.preparationDesignBuccal?.Name?.trim() ?? null,
			PreparationDesignLingualId: raw.preparationDesignLingual?.Id ?? null,
			PreparationDesignLingual: raw.preparationDesignLingual?.Name?.trim() ?? null,
			MarginDesignBuccalId: raw.marginDesignBuccal?.Id ?? null,
			MarginDesignBuccal: raw.marginDesignBuccal?.Name?.trim() ?? null,
			MarginDesignLingualId: raw.marginDesignLingual?.Id ?? null,
			MarginDesignLingual: raw.marginDesignLingual?.Name?.trim() ?? null,
			ShadeIncisal: getMeaningfulValue<string>(raw.incisal as Maybe<IdName> | Maybe<string>) as Maybe<string>,
			ShadeGingival: getMeaningfulValue<string>(raw.gingival as Maybe<IdName> | Maybe<string>) as Maybe<string>,
			StumpfShade: (raw.stumpShade?.Id ?? null) as Maybe<string>
		});
	}
}
